/**
 * SlotChip.jsx
 *
 * Electric-blue chip rendered inside a filled DSC slot card showing the picked
 * value (e.g. "Sandvik HLX1", "Reduction sleeve · R32", "R32", "Spherical").
 * NEW primitive — no legacy LWC equivalent. Visual-state improvement per
 * brainstorm wishlist #10 (locked 19 May 2026): "empty outline → electric-blue
 * chip when filled" replaces the legacy `empty-product` ↔ filled-card swap.
 *
 * Citations:
 *  - Plan §SP1.5 / §0 row `SlotChip.jsx`: "Renders an electric-blue rounded-rect
 *    chip inside a filled slot card showing the picked value."
 *  - Brainstorm wishlist #10: visual state improvement.
 *  - Sandvik PWA template rules: no rounded corners (`rounded-none`), electric
 *    blue `#1441F5`, `font-sandvik-text`.
 *
 * Behaviour contract:
 *  - Returns null when `label` is falsy / empty.
 *  - Rendered inline with `inline-flex` so the parent slot card controls layout.
 *  - Carries the optional onClear handler — when present, renders an x-button
 *    suffix that calls onClear (mirrors legacy LWC "Remove from DSC" behaviour
 *    in `dscProductCard.html` lines 234-241 `remove-product` block).
 *
 * @module components/dsc/SlotChip
 * @since 2026-05-20 (Group E SP2 — shared DSC primitives)
 */

import { X } from 'lucide-react';

/**
 * @param {Object} props
 * @param {string} props.label — chip text; renders nothing when falsy.
 * @param {() => void} [props.onClear] — optional clear handler.
 * @param {string} [props.dataTestid] — optional test id override.
 */
export default function SlotChip({ label, onClear, dataTestid = 'slot-chip' }) {
  if (!label) return null;
  return (
    <span
      data-testid={dataTestid}
      className="inline-flex items-center gap-2 px-3 py-1 bg-electric-blue text-white font-sandvik-text text-sm font-medium rounded-none"
    >
      <span className="truncate max-w-[14rem]">{label}</span>
      {typeof onClear === 'function' && (
        <button
          type="button"
          onClick={onClear}
          aria-label="Clear selection"
          data-testid="slot-chip-clear"
          className="inline-flex items-center justify-center w-4 h-4 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white"
        >
          <X size={12} />
        </button>
      )}
    </span>
  );
}
